import { Collapse, Divider } from 'antd';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { Invoice, InvoiceItemType, generateInvoiceCreditNotePdfLink, generateInvoicePdfLink } from 'entities/Invoice';
import { getLoggedUserData, useRole, getUserName } from 'entities/User';
import { ReactComponent as CollapseArrow } from 'shared/assets/icons/CollapseArrow.svg';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { SendInvoiceByEmail } from 'features/SendInvoiceByEmail';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import s from './InvoiceCard.module.scss';
import { PaymentModal } from './PaymentModal';
import { CreditInvoice } from 'features/CreditInvoice';
import { MarkAsPaidInvoice } from 'features/MarkAsPaidInvoice';
import { DepositRefundNote } from 'entities/Deposit';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { AutochargeStatus } from 'entities/Contract';
import { ChargeModal } from './ChargeModal';
import { WarnTag } from 'shared/ui/WarnTag';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatVatRate } from 'shared/utils/helpers/formatVatRate';

interface InvoiceCardProps {
  invoice: Invoice;
  defaultOpened?: boolean;
}

export const InvoiceCard: FC<InvoiceCardProps> = memo((props) => {
  const { invoice, defaultOpened } = props;
  const { user, contract } = invoice;

  const { t } = useAppTranslation('contracts');

  const loggedUserData = useAppSelector(getLoggedUserData);

  const isSelfInvoice = loggedUserData?.userId === user.userId;

  const [isOpenedModal, setOpenedModal] = useState(false);
  const [isChargeModalOpen, setChargeModalOpen] = useState(false);
  const [isCollapseOpen, setCollapseOpen] = useState(defaultOpened || false);

  const isInvoiceRelevant = /* contract ? !contract.leaveDate :  */ true; // temporary we allow to credit invoices in closed contracts
  const isAutochargesEnabled = contract?.autocharges === AutochargeStatus.ENABLED;

  const { scrollToElement, scrollRef } = useScrollToElement('end', 1000);

  useLayoutEffect(() => {
    defaultOpened && scrollToElement();
  }, [defaultOpened, scrollToElement]);

  const toggleCollapse = useCallback(() => {
    setCollapseOpen((prev) => !prev);
  }, []);

  const openPaymentModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closePaymentModal = useCallback((): void => {
    setOpenedModal(false);
  }, []);

  const openChargeModal = useCallback((): void => {
    setChargeModalOpen(true);
  }, []);

  const closeChargeModal = useCallback((): void => {
    setChargeModalOpen(false);
  }, []);

  const currencySymbol = useGetCurrencySymbol();

  const { isManagement, isPartner } = useRole();

  const isLateChargeInvoice = invoice.invoiceItems.some(({ invoiceItemType }) => invoiceItemType === InvoiceItemType.LATE_CHARGE);

  const isDepositInvoice =
    invoice.invoiceItems.find(({ invoiceItemType }) => invoiceItemType === InvoiceItemType.DEPOSIT) && invoice.invoiceItems.length === 1;

  const isPersonalInvoice = !contract;

  const splitedDescription = splitDocumentDescription(invoice.description || '');

  const isInvoicePaid = invoice.debt === 0;
  const isPaymentNeeded = !isInvoicePaid;
  const paymentsAmount = roundNumber(
    invoice.payments.reduce((acc, payment) => {
      if (payment.creditNote || payment.depositRefundNote) {
        return payment.creditNote
          ? acc + roundNumber(payment.amount - payment.creditNote.creditedAmount)
          : acc + roundNumber(payment.amount - (payment.depositRefundNote as DepositRefundNote).refundAmount);
      }

      return acc + payment.amount;
    }, 0),
  );
  const creditedAmount = roundNumber(invoice.creditNotes.reduce((acc, curr) => acc + curr.creditedAmount, 0));

  const invoiceTotalsTableRows = useMemo(() => {
    const rows = [
      { label: t('Total'), value: `${formatPrice(invoice.amount)} ${currencySymbol}`, emptyValueSymbol: `0 ${currencySymbol}` },
    ];

    if (paymentsAmount) {
      rows.push({
        label: t('Paid amount'),
        value: `${formatPrice(paymentsAmount)} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      });
    }

    if (creditedAmount) {
      rows.push({
        label: t('Credited amount'),
        value: `${formatPrice(creditedAmount)} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      });
    }

    if (invoice.debt) {
      rows.push({ label: t('Debt'), value: `${formatPrice(invoice.debt)} ${currencySymbol}`, emptyValueSymbol: `0 ${currencySymbol}` });
    }

    return rows;
  }, [creditedAmount, currencySymbol, invoice.amount, invoice.debt, paymentsAmount, t]);

  const InvoiceContent = useCallback((): JSX.Element => {
    return (
      <div>
        <div className="text-lg font-semibold mb-4 font-tektur">{t('Customer details')}</div>
        <div className="flex flex-col">
          <span className="text-sm text-grey">{t('Client')}</span>
          <span className="text-primaryLight font-tektur">{getUserName(user)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm text-grey">{t('Mail')}</span>
          <span className="text-primaryLight font-tektur">{invoice.invoiceEmail}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm text-grey">{t('Address')}</span>
          <span className=" text-primaryLight font-tektur">{invoice.invoiceAddress}</span>
        </div>
        <Divider className="border-primary" />
        {invoice.dateFrom && invoice.dateTo && (
          <>
            <div className="text-lg font-semibold font-tektur">{t('Invoicing period')}</div>
            <BulletsTable
              theme="clear"
              rows={[
                { label: t('From'), value: getClientDateFormat(invoice.dateFrom) },
                { label: t('To'), value: getClientDateFormat(invoice.dateTo) },
              ]}
            />
            <Divider className="border-primary" />
          </>
        )}
        <div>
          {invoice.invoiceItems.map((item) => {
            const rows = [
              {
                label: t('Netto amount'),
                value: `${formatPrice(roundNumber(item.initialPrice))} ${currencySymbol}`,
                emptyValueSymbol: `0 ${currencySymbol}`,
              },
              {
                label: t('Discount'),
                value: `${item.discountPercent}%`,
                emptyValueSymbol: '0%',
              },
              {
                label: t('VAT'),
                value: formatVatRate(item.vatRatePercent, t),
              },
              {
                label: t('Brutto amount'),
                value: `${formatPrice(roundNumber(item.totalPrice))} ${currencySymbol}`,
                emptyValueSymbol: `0 ${currencySymbol}`,
              },
            ];

            if (item.description) {
              const splitedDescription = splitDocumentDescription(item.description);

              rows.push({
                label: t('Description'),
                value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
                emptyValueSymbol: '-',
              });
            }

            return (
              <div key={item.invoiceItemId}>
                {item.invoiceItemType && (
                  <div className="mt-3 text-lg font-semibold font-tektur">
                    {t(item.invoiceItemType)}
                    {[InvoiceItemType.BOX, InvoiceItemType.OFFICE].includes(item.invoiceItemType)
                      ? ` #${item?.contractItem?.box?.name}`
                      : null}
                  </div>
                )}
                <BulletsTable theme="clear" rows={rows} />
              </div>
            );
          })}
        </div>
        <Divider className="border-primary" />
        <BulletsTable theme="clear" rows={invoiceTotalsTableRows} />

        {invoice.creditNotes.map((creditNote) => (
          <div key={creditNote.creditNoteId} className="flex flex-col my-3">
            <a
              href={generateInvoiceCreditNotePdfLink(invoice.invoiceId, creditNote.creditNoteId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-accent"
            >
              {t('Download Credit Note {{documentNumber}} PDF', { documentNumber: creditNote.documentNumber })}
            </a>
            {creditNote.issuedBy && isManagement && (
              <div className="flex gap-1">
                <span className="text-sm font-medium text-primaryLight">{t('Credited by')}</span>
                <span className="text-sm font-medium text-primaryLight">{getUserName(creditNote.issuedBy)}</span>
              </div>
            )}
          </div>
        ))}

        <div className="mt-2">
          {isPaymentNeeded && isInvoiceRelevant && (
            <div className="flex flex-col gap-3">
              {isManagement && (
                <div className="flex flex-col gap-3">
                  <MarkAsPaidInvoice invoice={invoice} action={<Button theme="secondary">{t('Mark as Paid')}</Button>} />
                  <CreditInvoice invoice={invoice} action={<Button theme="primary">{t('Credit Invoice')}</Button>} />
                  {isAutochargesEnabled && (
                    <Button theme="danger" onClick={openChargeModal}>
                      {t('Charge Invoice')}
                    </Button>
                  )}
                </div>
              )}
              {isSelfInvoice && (
                <>
                  {isAutochargesEnabled && invoice.dueDate && (
                    <WarnTag disableIcon textSize="medium">
                      {t('This Invoice will be charged automatically, but you can pay it manually')}
                    </WarnTag>
                  )}
                  <Button theme="danger" onClick={openPaymentModal}>
                    {t('Pay')}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 mt-2 py-4 bg-white">
          <a href={generateInvoicePdfLink(invoice.invoiceId)} target="_blank" rel="noopener noreferrer" className="font-medium text-accent">
            <Button containerClassName="w-full" theme="outlined" className="!text-grey !border-grey">
              {t('Download PDF')}
            </Button>
          </a>

          {!isPartner && (
            <SendInvoiceByEmail
              invoiceId={invoice.invoiceId}
              action={
                <Button theme="outlined" containerClassName="w-full" className="!text-grey !border-grey">
                  {t('Send by Email')}
                </Button>
              }
            />
          )}
        </div>
        <div className="cursor-pointer w-full flex justify-center" onClick={toggleCollapse}>
          <CollapseArrow
            className={classNames('fill-primary w-6 h-6 cursor-pointer transition-transform duration-700 ease-in-out', {
              '-rotate-90': isCollapseOpen,
              '!hidden': !isCollapseOpen,
            })}
          />
        </div>
      </div>
    );
  }, [
    t,
    user,
    invoice,
    invoiceTotalsTableRows,
    isPartner,
    isPaymentNeeded,
    isInvoiceRelevant,
    isManagement,
    isAutochargesEnabled,
    openChargeModal,
    isSelfInvoice,
    openPaymentModal,
    isCollapseOpen,
    toggleCollapse,
    currencySymbol,
  ]);

  return (
    <div ref={scrollRef}>
      <PaymentModal invoice={invoice} onClose={closePaymentModal} isOpen={isOpenedModal} />
      <ChargeModal invoice={invoice} onClose={closeChargeModal} isOpen={isChargeModalOpen} />
      <Collapse
        className={classNames(
          s.collapse /* , {
          [s.needToPay]: !isInvoicePaid,
          [s.paymentAndCreditNote]: creditedAmount && paymentsAmount,
          [s.partialPayment]: paymentsAmount && !isInvoicePaid && !creditedAmount,
          [s.partialCreditNote]: creditedAmount && !isInvoicePaid && !paymentsAmount,
          [s.partialPaymentAndCreditNote]: creditedAmount && paymentsAmount && !isInvoicePaid,
          [s.refundedOrCredited]: isInvoicePaid && creditedAmount,
          [s.completed]: isInvoicePaid && !creditedAmount,
        } */,
        )}
        activeKey={isCollapseOpen ? invoice.invoiceId : undefined}
        onChange={(keys) => {
          setCollapseOpen(!!keys.length);
        }}
        // defaultActiveKey={defaultOpened ? invoice.invoiceId : undefined}
        items={[
          {
            key: invoice.invoiceId,
            label: (
              <>
                <div className="font-semibold text-lg flex justify-between py-1 items-start">
                  <div className="font-semibold text-lg flex justify-between flex flex-col font-tektur">
                    {t(!isDepositInvoice ? 'Invoice' : 'Deposit')} {invoice.documentNumber}
                    {isLateChargeInvoice && <div className="text-base font-semibold">({t('Late charge')})</div>}
                  </div>

                  <div
                    className={classNames('flex gap-1 items-center text-sm rounded-xl px-2 py-0.5', {
                      'text-success bg-successLight': invoice.debt === 0 && invoice.payments.length,
                      'text-error bg-errorLight': invoice.debt > 0,
                      'text-primary bg-secondary': invoice.debt === 0 && !invoice.payments.length,
                    })}
                  >
                    {t(`${invoice.debt === 0 && invoice.payments.length ? 'Paid' : invoice.debt > 0 ? 'Not paid' : 'Credited'}`)}
                  </div>
                </div>
                {isManagement && invoice.description && (
                  <div className="text-sm text-primaryLight">
                    {isPersonalInvoice || isLateChargeInvoice
                      ? `${t(splitedDescription.description || invoice.description)} ${splitedDescription.documentNumber}`
                      : t(invoice.description)}
                  </div>
                )}
                {invoice.dueDate && invoice.debt !== 0 && (
                  <div className="text-sm text-primaryLight">
                    {isAutochargesEnabled ? t('Will be charged until') : t('Pay until')} {getClientDateFormat(invoice.dueDate)}
                  </div>
                )}
              </>
            ),
            children: <InvoiceContent />,
          },
        ]}
        expandIcon={() => (
          <CollapseArrow
            onClick={toggleCollapse}
            className={classNames(
              'fill-primary absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-6 cursor-pointer transition-transform duration-700 ease-in-out',
              {
                '!hidden': isCollapseOpen,
                'rotate-90': !isCollapseOpen,
              },
            )}
          />
        )}
      />
    </div>
  );
});
